import { closeAllModals } from './navigationController';
import { closeCommunityIdeaDialog } from './communityIdeaDialogController';
import { closeAiSubmissionDialog } from './aiSubmissionDialogController';
import { closeChapterDirectory } from './chapterDirectoryController';
import { rememberCelebrationPoint } from './celebration';
import { normalizePrototypeAction } from './normalizePrototypeAction';
import { resolveClientAccess, type ClientAccessContext } from './accessResolver';
import { presentAccessDecision } from './presentAccessDecision';
import { runPrototypeAction, runPrototypeChangeAction } from './prototypeActionRouter';
import { recordRuntimeError } from './prototypeRuntimeSupport';
import type { PrototypeState } from './prototypeHydration';
import { stageAccessDecisionTrace } from './accessDecisionTrace';
import { resolveMediaAccessDecisionForState } from './mediaPlayerAccessGate';
import { mountedFor, toggleMediaPlayer } from './mediaPlayer';
import { isImeCompositionEvent } from './inputComposition';
import { isKeyboardFocusedControl, attachKeyboardModalityTracking, noteKeyboardNavigation } from './keyboardFocus';

function shouldBlockAction(root: HTMLElement, state: PrototypeState, action: string, source: HTMLElement): boolean {
	const normalized = normalizePrototypeAction(action, source);
	if (!normalized.action) return false;
	const resource = normalized.resource ? { ...normalized.resource } : undefined;
	const isMediaAction = normalized.action === 'media.audio.play' || normalized.action === 'media.video.play';
	// Global player controls describe the mounted audio owner, not necessarily
	// the detail page underneath them. Resolve the same source identity here as
	// the eventual video/audio route so an early UI gate cannot block or allow
	// the chapter currently being browsed (B) while A is actually playing.
	const mountedAudio = isMediaAction && source.closest('#webPlayer') ? mountedFor(root, 'audio') : undefined;
	const topicId = mountedAudio?.topicId ?? state.runtime.activeCourseId;
	const chapterIndex = mountedAudio?.chapterIndex ?? resource?.index ?? state.runtime.activeChapterIndex;
	const decision = isMediaAction && resource?.type === 'chapter' && topicId
		? resolveMediaAccessDecisionForState(root, state, normalized.action === 'media.audio.play' ? 'audio' : 'video', topicId, chapterIndex)
		: resolveClientAccess(normalized.action, {
			isLoggedIn: state.isLoggedIn,
			plan: state.effectiveAccessPlan,
			resource,
		} satisfies ClientAccessContext);
	const handled = presentAccessDecision(root, state, decision, {
		rawAction: action,
		policyAction: decision.action,
	});
	if (!handled) stageAccessDecisionTrace(normalized.action, action, decision.kind === 'allow' ? 'allow' : decision.kind === 'partial' ? 'partial' : 'deny');
	return handled;
}

/**
 * A token-backed first start necessarily finishes after the original click.
 * Once that source is mounted, however, the visible Play buttons must invoke
 * HTMLMediaElement.play() in this exact trusted click stack. Sending these
 * controls through the generic async router loses Android's transient user
 * activation a second time.
 */
function toggleMountedMediaFromTrustedGesture(root: HTMLElement, state: PrototypeState, action: string, source: HTMLElement): boolean {
	if (/\btoggleVideoPlay\(\)/.test(action)) {
		if (!source.closest('#videoFullscreen') || !mountedFor(root, 'video')) return false;
		toggleMediaPlayer(root, 'video');
		return true;
	}
	const audio = mountedFor(root, 'audio');
	if (!audio) return false;
	if (/\btogglePlay\(\)/.test(action) && source.closest('#webPlayer')) {
		toggleMediaPlayer(root, 'audio');
		return true;
	}
	// A first token response can leave the chapter button visibly paused. Its
	// next tap is also a retry gesture, not a request to mint another token.
	const requestedChapter = Number(action.match(/\bplayTopicChapter\(\s*(\d+)/)?.[1]
		?? (action.includes('playArticleAudio') || action.includes('showPlayer') ? state.runtime.activeChapterIndex : NaN));
	if (!Number.isInteger(requestedChapter) || requestedChapter !== audio.chapterIndex) return false;
	if (audio.topicId !== state.runtime.activeCourseId) return false;
	if (!/\b(?:playTopicChapter|playArticleAudio|showPlayer)\b/.test(action)) return false;
	toggleMediaPlayer(root, 'audio');
	return true;
}

/** Bind all delegated prototype events and return a complete teardown. */
export function attachPrototypeEventBindings(root: HTMLElement, state: PrototypeState): () => void {
	const submittingForms = new WeakSet<HTMLElement>();
	const onClick = (event: MouseEvent) => {
		const target = event.target as HTMLElement | null;
		const dropdown = root.querySelector<HTMLElement>('#avatarDropdown');
		if (dropdown?.classList.contains('open') && !target?.closest('.avatar-dropdown-wrap, #avatarDropdown')) {
			dropdown.classList.remove('open');
		}

		const clickable = target?.closest<HTMLElement>("[data-proto-click], a[href='#'], .modal-overlay");
		if (!clickable) return;
		// Auxiliary anchor activation (Cmd/Ctrl-click, shift-click, middle-click)
		// must keep the browser's open-in-new-tab/window default; only a plain
		// click routes through the prototype action.
		if (clickable.matches('a[href]') && !clickable.matches("a[href='#']")
			&& (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
		const action = clickable.dataset.protoClick ?? '';
		if (clickable.matches("a[href='#']") || action) event.preventDefault();
		rememberCelebrationPoint(event);
		if (clickable.classList.contains('modal-overlay') && clickable === event.target) {
			if (clickable.id === 'chapterDirectoryModal') {
				closeChapterDirectory(root);
				return;
			}
			if (clickable.id === 'communityIdeaDetailModal') {
				closeCommunityIdeaDialog(root);
				return;
			}
			if (clickable.id === 'aiSubmissionDetailModal') {
				closeAiSubmissionDialog(root);
				return;
			}
			// Protected action modals (e.g. paid-upgrade confirm/resume) dismiss
			// only via their explicit buttons or Escape. A backdrop click must
			// not tear down the whole stack, including the pricing panel behind
			// them (§14).
			if (!clickable.classList.contains('modal-overlay--protected') && !clickable.querySelector('.modal-close, .btn-icon')) closeAllModals(root);
			return;
		}
		if (!action || shouldBlockAction(root, state, action, clickable)) return;
		if (toggleMountedMediaFromTrustedGesture(root, state, action, clickable)) return;
		void runPrototypeAction(action, root, clickable, state).catch((error) => recordRuntimeError(state.runtime, error));
	};

	const onChange = (event: Event) => {
		const source = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-proto-change]');
		const action = source?.dataset.protoChange ?? '';
		if (!source || !action || shouldBlockAction(root, state, action, source)) return;
		void runPrototypeChangeAction(action, event, root, source, state).catch((error) => recordRuntimeError(state.runtime, error));
	};

	const onInput = (event: Event) => {
		const source = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-proto-input]');
		const action = source?.dataset.protoInput ?? '';
		if (!source || !action || shouldBlockAction(root, state, action, source)) return;
		void runPrototypeAction(action, root, source, state).catch((error) => recordRuntimeError(state.runtime, error));
	};
	const onSubmit = (event: SubmitEvent) => {
		const source = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-proto-submit]');
		const action = source?.dataset.protoSubmit ?? '';
		if (!source || !action) return;
		event.preventDefault();
		if (submittingForms.has(source) || shouldBlockAction(root, state, action, source)) return;
		submittingForms.add(source);
		void runPrototypeAction(action, root, source, state)
			.catch((error) => recordRuntimeError(state.runtime, error))
			.finally(() => submittingForms.delete(source));
	};

	root.addEventListener('click', onClick);
	root.addEventListener('submit', onSubmit);
	const onKeydown = (event: KeyboardEvent) => {
		// A local text-input shortcut has already handled ordinary Enter. An IME
		// candidate-confirmation Enter belongs to the browser in every case.
		if (event.defaultPrevented || isImeCompositionEvent(event)) return;
		const target = event.target as HTMLElement | null;
		const tab = target?.closest<HTMLElement>('[data-community-role="filters"] [role="tab"]');
		if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
			noteKeyboardNavigation();
			const tabs = Array.from(tab.closest<HTMLElement>('[data-community-role="filters"]')?.querySelectorAll<HTMLElement>('[role="tab"]') ?? []);
			const current = tabs.indexOf(tab);
			const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
			event.preventDefault(); tabs[next]?.focus(); tabs[next]?.click(); return;
		}
		if ((event.key === 'Enter' || event.key === ' ') && target?.dataset.keyboardAction === 'true') {
			// A pointer click leaves the control focused without a focus ring;
			// Space must not silently re-activate it. Blur instead of clicking:
			// the keydown default (page scroll) survives, the keyup click dies
			// with the focus it needed. Keyboard focus keeps activation.
			if (event.key === ' ' && !isKeyboardFocusedControl(target)) {
				target.blur();
				return;
			}
			event.preventDefault();
			target.click();
			return;
		}
		if (event.key === ' ' || event.key === 'Spacebar') {
			// Native buttons, links and menus behave like the div-based actions
			// above: a mouse click leaves them focused without a focus ring, and
			// the browser re-activates them on Space keyup (nav tab re-navigates,
			// list reloads). Blur the pointer-focused control so Space keeps its
			// native meaning — page scroll, or the audio player toggle — exactly
			// like after the control had been left by Tab.
			const control = target?.closest<HTMLElement>('button, a[href], summary, [role="button"], [role="link"], [role="tab"], [role="switch"], [role="option"]');
			if (control && !isKeyboardFocusedControl(control)) control.blur();
			return;
		}
		if (event.key !== 'Enter') return;
		// For textareas, only blur on Enter without modifier (mobile "Go"/"Done").
		// Cmd/Ctrl+Enter should insert a newline, not blur.
		if (target instanceof HTMLInputElement) {
			setTimeout(() => target.blur(), 0);
		} else if (target instanceof HTMLTextAreaElement && !event.metaKey && !event.ctrlKey) {
			// Allow the textarea's default Enter (newline) on desktop, but on
			// mobile the "Done"/"Go" key sends Enter without modifiers - blur
			// to dismiss the keyboard. Use a microtask so the keydown finishes
			// processing first.
			setTimeout(() => target.blur(), 0);
		}
	};
	root.addEventListener('keydown', onKeydown);
	root.addEventListener('change', onChange);
	root.addEventListener('input', onInput);

	// iOS Safari 10+ ignores user-scalable=no and maximum-scale for
	// accessibility, so it still allows pinch-to-zoom. Prevent gesture
	// scaling to keep the page at 1:1 on mobile.
	// Guard typeof document/window so Node test environments (no DOM) don't
	// crash when attachPrototypeEventBindings is called with a mock root.
	const hasDom = typeof document !== 'undefined' && typeof window !== 'undefined';
	const onGestureStart = (e: Event) => e.preventDefault();
	const onGestureChange = (e: Event) => e.preventDefault();

	// Prevent iOS focus-driven zoom: when an input is focused, scroll it
	// into view so iOS Safari doesn't need to auto-zoom to bring it above
	// the keyboard. This addresses the community search box (and all other
	// inputs) zooming the page on tap.
	const onFocusIn = (e: FocusEvent) => {
		const target = e.target as HTMLElement;
		if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) {
			// Defer to next frame so the keyboard has time to appear, then
			// scroll the input into the visible area without iOS zooming.
			requestAnimationFrame(() => {
				try { target.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch { /* ignore */ }
			});
		}
	};

	// Snap back to 1:1 after a manual pinch-zoom. The previous approach
	// (rewriting the viewport meta content) does not reliably reset an
	// already-applied iOS zoom. Instead, use a short rAF loop that checks
	// visualViewport.scale and forces it back to 1 by temporarily clamping
	// the viewport meta to maximum-scale=1, then restoring it.
	let snapBackScheduled = false;
	const snapBackToUnitScale = () => {
		if (snapBackScheduled) return;
		snapBackScheduled = true;
		let attempts = 0;
		const tryReset = () => {
			attempts++;
			const vv = window.visualViewport;
			if (!vv || Math.abs(vv.scale - 1) <= 0.01 || attempts > 10) {
				snapBackScheduled = false;
				return;
			}
			// Clamp the viewport meta to force scale=1, then restore after
			// a short delay. iOS honors maximum-scale changes on next layout.
			const meta = document.querySelector('meta[name="viewport"]');
			if (meta) {
				const original = meta.getAttribute('content') || '';
				meta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
				setTimeout(() => meta.setAttribute('content', original || 'width=device-width, initial-scale=1.0, user-scalable=no, viewport-fit=cover'), 50);
			}
			requestAnimationFrame(tryReset);
		};
		requestAnimationFrame(tryReset);
	};
	const onTouchEnd = () => {
		if (window.visualViewport && Math.abs(window.visualViewport.scale - 1) > 0.01) {
			snapBackToUnitScale();
		}
	};
	// Also listen for visualViewport resize (fires during/after pinch) to
	// catch zoom that touchend misses.
	const onViewportResize = () => {
		if (window.visualViewport && Math.abs(window.visualViewport.scale - 1) > 0.01) {
			snapBackToUnitScale();
		}
	};
	if (hasDom) {
		document.addEventListener('gesturestart', onGestureStart, { passive: false });
		document.addEventListener('gesturechange', onGestureChange, { passive: false });
		document.addEventListener('focusin', onFocusIn, { passive: true });
		window.addEventListener('touchend', onTouchEnd, { passive: true });
		window.visualViewport?.addEventListener('resize', onViewportResize, { passive: true });
		window.visualViewport?.addEventListener('scroll', onViewportResize, { passive: true });
	}
	const detachModalityTracking = hasDom
		? attachKeyboardModalityTracking(root.ownerDocument ?? document)
		: () => undefined;

	return () => {
		root.removeEventListener('click', onClick);
		root.removeEventListener('submit', onSubmit);
		root.removeEventListener('keydown', onKeydown);
		root.removeEventListener('change', onChange);
		root.removeEventListener('input', onInput);
		detachModalityTracking();
		if (hasDom) {
			document.removeEventListener('gesturestart', onGestureStart);
			document.removeEventListener('gesturechange', onGestureChange);
			document.removeEventListener('focusin', onFocusIn);
			window.removeEventListener('touchend', onTouchEnd);
			window.visualViewport?.removeEventListener('resize', onViewportResize);
			window.visualViewport?.removeEventListener('scroll', onViewportResize);
		}
	};
}
